'use client';

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth/context';
import { buildDemoPrivacyReplacements, isDemoPrivacyUser, type DemoPrivacyReplacement } from '@/lib/demo-privacy';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function maskWith(replacements: DemoPrivacyReplacement[], value: string): string {
  let next = value;
  for (const replacement of replacements) {
    if (!replacement.from || !next.toLowerCase().includes(replacement.from.toLowerCase())) continue;
    next = next.replace(new RegExp(escapeRegExp(replacement.from), 'gi'), replacement.to);
  }
  return next;
}

function shouldSkipNode(node: Node): boolean {
  const element = node.nodeType === Node.ELEMENT_NODE
    ? node as Element
    : node.parentElement;
  if (!element) return false;
  return !!element.closest('script, style, noscript');
}

function applyDemoPrivacyMask(replacements: DemoPrivacyReplacement[]) {
  if (!replacements.length || typeof document === 'undefined') return;

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (shouldSkipNode(node)) return NodeFilter.FILTER_REJECT;
      const value = node.nodeValue || '';
      return replacements.some((replacement) => value.toLowerCase().includes(replacement.from.toLowerCase()))
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  });

  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);
  for (const textNode of textNodes) {
    const value = textNode.nodeValue;
    if (value) textNode.nodeValue = maskWith(replacements, value);
  }

  const elements = document.body.querySelectorAll<HTMLElement>('[title], [aria-label], [alt], [placeholder], input, textarea');
  for (const element of Array.from(elements)) {
    if (shouldSkipNode(element)) continue;

    for (const attr of ['title', 'aria-label', 'alt', 'placeholder']) {
      const value = element.getAttribute(attr);
      if (!value) continue;
      const masked = maskWith(replacements, value);
      if (masked !== value) element.setAttribute(attr, masked);
    }

    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      const masked = maskWith(replacements, element.value);
      if (masked !== element.value) element.value = masked;
    }
  }
}

export default function DemoPrivacyMask() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [serverReplacements, setServerReplacements] = useState<DemoPrivacyReplacement[]>([]);
  const demoPrivacyActive = !isLoading && isAuthenticated && isDemoPrivacyUser(user);

  const localReplacements = useMemo(
    () => buildDemoPrivacyReplacements({ user }),
    [user],
  );

  const replacements = useMemo(() => {
    if (!demoPrivacyActive) return [];
    const seen = new Set<string>();
    return [...serverReplacements, ...localReplacements].filter((replacement) => {
      const key = replacement.from.toLowerCase();
      if (!replacement.from || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [demoPrivacyActive, localReplacements, serverReplacements]);

  useEffect(() => {
    if (!demoPrivacyActive) return;

    let cancelled = false;
    fetch('/api/demo-privacy/context', { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((payload) => {
        if (cancelled || !payload?.enabled || !Array.isArray(payload.replacements)) return;
        setServerReplacements(payload.replacements);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [demoPrivacyActive]);

  useEffect(() => {
    if (!replacements.length || typeof document === 'undefined') return;

    applyDemoPrivacyMask(replacements);
    const timers = [100, 350, 900, 1800].map((delay) => window.setTimeout(() => {
      applyDemoPrivacyMask(replacements);
    }, delay));

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => applyDemoPrivacyMask(replacements));
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['title', 'aria-label', 'alt', 'placeholder', 'value'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [replacements]);

  return null;
}
